import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'

import { useMediaCardData } from '~hooks'
import { getURI } from '~utilities'

import TrendingQuestions from '~components/home/trending-questions'

const quotemarkStyles = {
  fontFamily: 'Arial',
  fontSize: '5rem',
  color: '#afcedd'
}

const Sidebar = ({ sidebarContent, showFeaturedVideo, showFeaturedActivities }) => {
  const { locale } = useIntl()
  const key = locale.toLowerCase()

  const data = useStaticQuery(staticQuery)
  const defaultSidebarContent = data.defaultSidebarContent.nodes.find(({ node_locale }) => node_locale === locale)

  const {
    pageFeaturedDownloadLabel,
    pageFeaturedDownloadTitle,
    pageFeaturedDownloadImage,
    pageFeaturedDownloadAsset,
    pageFeaturedDownloadLink,
    pageFeaturedDownloadCta,
    pageFeaturedVideo,
    pageFeaturedService,
    pageFeaturedActivities,
    pageFeaturedQuote,
    pageFeaturedImage,
    pageFeaturedImageLink
  } = sidebarContent

  const {
    defaultFeaturedDownloadLabel,
    defaultFeaturedDownloadTitle,
    defaultFeaturedDownloadImage,
    defaultFeaturedDownloadAsset,
    defaultFeaturedDownloadLink,
    defaultFeaturedDownloadCta,
    defaultFeaturedVideo,
    defaultFeaturedService,
    defaultFeaturedActivities,
    defaultFeaturedQuote,
    defaultFeaturedImage,
    defaultFeaturedImageLink
  } = defaultSidebarContent

  // ✅ Featured clickable image
  const featuredImagePath = pageFeaturedImage
    ? `${pageFeaturedImage.file?.url}?fm=jpg&fl=progressive&w=600&q=70`
    : defaultFeaturedImage?.file?.url
      ? `${defaultFeaturedImage.file.url}?fm=jpg&fl=progressive&w=600&q=70`
      : null

  const featuredImageLink = pageFeaturedImageLink || defaultFeaturedImageLink || null

  // Helper: determine if link is internal
  const isInternalLink = featuredImageLink && (featuredImageLink.startsWith('/') || featuredImageLink.includes(`/${key}/`))

  // Featured download
  const featuredDownloadLabel = pageFeaturedDownloadLabel || defaultFeaturedDownloadLabel
  const featuredDownloadTitle = pageFeaturedDownloadTitle || defaultFeaturedDownloadTitle
  const featuredDownloadImagePath = pageFeaturedDownloadImage
    ? `${pageFeaturedDownloadImage.file?.url}?fm=jpg&fl=progressive&w=320&q=60`
    : `${defaultFeaturedDownloadImage.file.url}?fm=jpg&fl=progressive&w=320&q=60`
  const featuredDownloadFileName =
    pageFeaturedDownloadAsset?.file?.fileName || defaultFeaturedDownloadAsset.file.fileName
  const featuredDownloadLink =
    pageFeaturedDownloadAsset?.file?.url ||
    pageFeaturedDownloadLink ||
    defaultFeaturedDownloadAsset.file.url ||
    defaultFeaturedDownloadLink
  const featuredDownloadCta = pageFeaturedDownloadCta || defaultFeaturedDownloadCta

  // Featured video
  const featuredVideo = pageFeaturedVideo || defaultFeaturedVideo
  const featuredVideoTitle = featuredVideo.title
  const featuredVideoImagePath = useMediaCardData(featuredVideo.contentful_id)?.image

  // Featured service
  const featuredService = pageFeaturedService || defaultFeaturedService
  const featuredServiceTitle = featuredService.name
  const featuredServiceDescription = featuredService.headline

  // Featured activities
  const featuredActivity1 = pageFeaturedActivities?.[0] || defaultFeaturedActivities[0]
  const featuredActivity2 = pageFeaturedActivities?.[1] || defaultFeaturedActivities[1]
  const featuredActivity3 = pageFeaturedActivities?.[2] || defaultFeaturedActivities[2]

  // Featured quote
  const featuredQuote = pageFeaturedQuote || defaultFeaturedQuote
  const featuredQuoteContent = featuredQuote.quote.quote
  const featuredQuoteAttributionLine1 = featuredQuote.person
  const featuredQuoteAttributionLine2 = featuredQuote.personSubline || ''

  return (
    <div className='lg:w-1/4 lg:pl-4 flex-shrink-0'>

      {/* ✅ Clickable Featured Image (internal or external) */}
      {featuredImagePath && (
        <div className='mb-8 p-4 bg-white shadow-center-xl'>
          {featuredImageLink ? (
            isInternalLink ? (
              <Link to={featuredImageLink}>
                <img
                  src={featuredImagePath}
                  className='w-full h-auto object-cover object-center'
                  alt='Featured'
                />
              </Link>
            ) : (
              <a href={featuredImageLink} target='_blank' rel='noopener noreferrer'>
                <img
                  src={featuredImagePath}
                  className='w-full h-auto object-cover object-center'
                  alt='Featured'
                />
              </a>
            )
          ) : (
            <img
              src={featuredImagePath}
              className='w-full h-auto object-cover object-center'
              alt='Featured'
            />
          )}
        </div>
      )}

      {/* Featured Video */}
      {showFeaturedVideo && (
        <div className='mb-8 p-4 bg-white shadow-center-xl'>
          <div className='aspect-ratio-16/9 overflow-hidden'>
            <img
              src={featuredVideoImagePath}
              className='w-full h-full object-cover object-center'
              alt='N/A'
              aria-hidden
            />
          </div>
          <div className='my-4 text-xs tracking-wider uppercase'>
            <FormattedMessage id='video-label' />
          </div>
          <Link to={getURI(featuredVideo, key)} className='font-extrabold text-lg text-blue leading-snug'>
            {featuredVideoTitle}
          </Link>
        </div>
      )}

      {/* Featured Download */}
      <div className='mb-8 p-4 bg-white shadow-center-xl'>
        <img src={featuredDownloadImagePath} className='w-full h-auto object-contain' alt='N/A' aria-hidden />
        <div className='my-4 text-xs tracking-wider uppercase'>{featuredDownloadLabel}</div>
        <div>
          <a
            href={featuredDownloadLink}
            download={new Boolean(featuredDownloadFileName)}
            target='_blank'
            rel='noopener noreferrer'
            className='font-extrabold text-lg text-blue leading-snug'
          >
            {featuredDownloadTitle}
          </a>
        </div>
        <div className='button button-pink mt-4 mb-0'>
          <a
            href={featuredDownloadLink}
            download={new Boolean(featuredDownloadFileName)}
            target='_blank'
            rel='noopener noreferrer'
          >
            {featuredDownloadCta} <em className='fas fa-caret-right ml-2' />
          </a>
        </div>
      </div>

      {/* Featured Service */}
      <div className='mb-8 p-4 bg-white shadow-center-xl'>
        <div className='mb-4 text-xs tracking-wider uppercase'>
          <FormattedMessage id='services-label' />
        </div>
        <Link to={getURI(featuredService, key)} className='headline-5 block'>
          {featuredServiceTitle}
        </Link>
        <Link to={getURI(featuredService, key)} className='body-5 mt-4'>
          {featuredServiceDescription}
        </Link>
      </div>

      {/* Featured Activities */}
      {showFeaturedActivities && (
        <div className='mb-8 p-4 bg-white shadow-center-xl'>
          <div className='mb-4 text-xs tracking-wider uppercase'>
            <FormattedMessage id='activities-label' />
          </div>
          <div className='headline-5'>
            <FormattedMessage id='activities-title' />
          </div>
          <div className='contentful'>
            <ol className='ordered-list ordered-list-sidebar mt-4'>
              {[featuredActivity1, featuredActivity2, featuredActivity3].map((activity, index) => (
                <li key={index}>
                  <Link to={getURI(activity, key)} className='font-bold text-sm text-blue'>
                    {activity.title}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Featured Quote */}
      <div className='mb-8 p-4 bg-cyan-lightest shadow-center-xl'>
        <div className='mb-4 text-xs tracking-wider uppercase'>
          <FormattedMessage id='reviews-label' />
        </div>
        <div className='headline-5'>
          <FormattedMessage id='reviews-title' />
        </div>
        <div className='flex flex-col'>
          <div className='relative z-0 h-10 -ml-3 -mt-5 font-bold opacity-50' style={quotemarkStyles} aria-hidden>
            &ldquo;
          </div>
          <div className='mt-4 relative z-10 text-sm italic leading-tight'>{featuredQuoteContent}</div>
          <div className='mt-4 flex text-xs leading-snug'>
            <div>
              <span className='block font-bold'>{featuredQuoteAttributionLine1}</span>
              {featuredQuoteAttributionLine2 && <span>{featuredQuoteAttributionLine2}</span>}
            </div>
          </div>
        </div>
        <div className='button button-blue-clear mt-4 mb-0'>
          <Link to={`/${key}/activities/`}>
            <FormattedMessage id='see-more-activities' /> <em className='fas fa-caret-right ml-2' />
          </Link>
        </div>
      </div>

      {/* Trending Questions */}
      <div className='mb-8 bg-white shadow-center-xl'>
        <div className='p-4 bg-royal-purple'>
          <div className='headline-5 text-white'>
            <FormattedMessage id='trending-questions' />
          </div>
        </div>
        <TrendingQuestions sidebar />
      </div>
    </div>
  )
}

Sidebar.defaultProps = {
  sidebarContent: {
    pageFeaturedDownloadLabel: null,
    pageFeaturedDownloadTitle: null,
    pageFeaturedDownloadImage: null,
    pageFeaturedDownloadAsset: null,
    pageFeaturedVideo: null,
    pageFeaturedService: null,
    pageFeaturedActivities: null,
    pageFeaturedQuote: null,
    pageFeaturedImage: null,
    pageFeaturedImageLink: null
  },
  showFeaturedVideo: true,
  showFeaturedActivities: true
}

Sidebar.propTypes = {
  sidebarContent: PropTypes.object,
  showFeaturedVideo: PropTypes.bool,
  showFeaturedActivities: PropTypes.bool
}

export default Sidebar

const staticQuery = graphql`
  query {
    defaultSidebarContent: allContentfulSidebar(filter: { title: { eq: "Global Sidebar" } }) {
      nodes {
        node_locale
        defaultFeaturedImage: featuredImage {
          file {
            url
          }
        }
        defaultFeaturedImageLink: featuredImageLink
        defaultFeaturedDownloadLabel: featuredDownloadLabel
        defaultFeaturedDownloadTitle: featuredDownloadTitle
        defaultFeaturedDownloadImage: featuredDownloadImage {
          file {
            url
          }
        }
        defaultFeaturedDownloadAsset: featuredDownloadAsset {
          file {
            fileName
            url
          }
        }
        defaultFeaturedDownloadLink: featuredDownloadLink
        defaultFeaturedDownloadCta: featuredDownloadCta
        defaultFeaturedVideo: featuredVideo {
          title
          contentful_id
          internal {
            type
          }
          node_locale
          slug
          type
        }
        defaultFeaturedService: featuredService {
          name
          headline
          slug
          internal {
            type
          }
          node_locale
          slug
          type
        }
        defaultFeaturedActivities: featuredActivities {
          title
          slug
          internal {
            type
          }
          node_locale
          slug
          type
        }
        defaultFeaturedQuote: featuredQuote {
          quote {
            quote
          }
          person
          personSubline
        }
      }
    }
  }
`
